import { KeyEnum } from 'app/hooks/useKeyBinder';
import { useCreatePopup } from 'Components/Popups';
import Options from 'Components/Select/Options';
import Spinner from 'Components/Spinner';
import { ReactNode, useRef, useState } from 'react';
import CancelSelectionButton from '../CancelSelectionButton';
import DropDownButton from '../DropDownButton';
import SelectInputWindow from '../SelectInputWindow';

export default function SelectSearchDropDown<
  T extends { id: any; label?: string; value?: string },
  D,
>({
  value,
  valueId,
  onChange,
  onChangeId,
  options = [],
  optionItemSkin,
  selectedItemSkin,
  id,
  placeHolder = 'Select',
  searchFilter = (_, options) => {
    const search = _.toLowerCase();
    return options.filter((option) =>
      option.label?.toLowerCase().includes(search),
    );
  },
  className,
  clearable = true,
  disableItemStyle = false,
  searchable = true,
  showSelectedInOptions = true,
  optionsContainerStyle = '',
  borderInputColor = 'border-gray-100',
  dropDownArrowColor = 'text-gray-light',
  minHeight = '42px',
  isLoading,
  disabled = false,
}: {
  value?: T;
  valueId?: D;
  id?: string;
  disabled?: boolean;
  isLoading?: boolean;
  clearable?: boolean;
  showSelectedInOptions?: boolean;
  searchable?: boolean;
  onChange?: (changes: T) => void;
  onChangeId?: (changes: D) => void;
  searchFilter?: (text: string, pool: T[]) => T[];
  options?: T[];
  optionItemSkin: (option: T, isActive: boolean) => ReactNode;
  selectedItemSkin: (item: T) => ReactNode;
  className?: string;
  disableItemStyle?: boolean;
  optionsContainerStyle?: string;
  borderInputColor?: string;
  dropDownArrowColor?: string;
  minHeight?: string;
  placeHolder?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');

  const selectedItem = valueId ? options.find((i) => i.id == valueId) : value;
  const selectableItems = showSelectedInOptions
    ? options
    : options.filter((i) => (selectedItem ? selectedItem.id !== i.id : true));

  const optionItems = searchFilter(search, selectableItems);

  const onChangeHandle = (newValue: T) => {
    onChange && selectedItem?.id !== newValue?.id && onChange(newValue);
    onChangeId && selectedItem?.id !== newValue?.id && onChangeId(newValue?.id);
  };

  const SelectPopup = useCreatePopup();

  const openPopup = () => {
    !disabled && optionItems.length > 0 && SelectPopup.open();
  };
  return (
    <>
      <button
        disabled={disabled}
        type="button"
        onClick={() => {
          inputRef.current?.focus();
          openPopup();
        }}
        ref={SelectPopup.setAnchor}
        className={`form__input disabled:cursor-default disabled:select-text flex min-w-[60px] w-full justify-between items-center cursor-pointer ${className}`}
        // className={`px-3 min-w-[60px] rounded-xl flex flex-nowrap justify-between text-sm items-center border focus:border-gray-light ${
        //   disabled || optionItems.length === 0 || isLoading
        //     ? 'cursor-text '
        //     : 'focus-within:shadow cursor-pointer'
        // }   ${borderInputColor} ${className} `}
      >
        <div className="flex">
          {searchable && !!options.length && (
            <input
              id={id}
              ref={inputRef}
              className="outline-none focus:outline-none  pr-0.5"
              value={search}
              style={{
                width: search.length === 0 ? '0ch' : search.length + 2 + 'ch',
              }}
              onFocus={openPopup}
              onKeyDown={(e) => {
                if (e.key === KeyEnum.ESCAPE && selectableItems.length > 0) {
                  setSearch('');
                  inputRef.current?.blur();
                }
              }}
              onChange={(e) => {
                setSearch(e.target.value);
                openPopup();
              }}
              type="text"
            />
          )}
          {selectedItem && !search && (
            <div className="text-gray-dark line-clamp-1">
              {selectedItemSkin(selectedItem)}
            </div>
          )}
          {!selectedItem && !search && !isLoading && (
            <div className="text-gray-light">
              {optionItems.length === 0 ? 'No records found' : placeHolder}
            </div>
          )}
        </div>
        {!disabled && (
          <>
            {isLoading && <Spinner bounce={false} />}
            {!isLoading && (
              <div className="flex">
                {!selectedItem ||
                  (clearable && (
                    <CancelSelectionButton
                      onClick={() => {
                        setSearch('');
                        onChangeHandle(undefined as any);
                        SelectPopup.close();
                      }}
                    />
                  ))}

                {(!selectedItem || !clearable) && optionItems.length > 0 && (
                  <DropDownButton dropDownArrowColor={dropDownArrowColor} />
                )}
              </div>
            )}
          </>
        )}
      </button>

      <SelectPopup.Component matchAnchorWidth={true}>
        <SelectInputWindow>
          <Options
            className={optionsContainerStyle}
            disableItemStyle={disableItemStyle}
            value={selectedItem}
            onSelect={(item) => {
              setSearch('');
              onChangeHandle(item);
              SelectPopup.close();
              inputRef.current?.blur();
            }}
            optionItems={optionItems}
          >
            {optionItemSkin}
          </Options>
        </SelectInputWindow>
      </SelectPopup.Component>
    </>
  );
}
